import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      quote: "DARAJA.LABS transformed our entire digital infrastructure. Their expertise in cloud migration and cybersecurity gave us the competitive edge we needed. The team\'s attention to detail and commitment to excellence is unmatched.",
      author: "Jennifer Martinez",
      position: "CTO",
      company: "TechFlow Solutions",
      companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      rating: 5
    },
    {
      id: 2,
      quote: "Working with DARAJA.LABS was a game-changer for our startup. They delivered a scalable web application that perfectly aligned with our vision. Their agile approach and technical expertise exceeded our expectations.",
      author: "Michael Chen",
      position: "Founder & CEO",
      company: "InnovateTech",
      companyLogo: "https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      rating: 5
    },
    {
      id: 3,
      quote: "The data analytics solution provided by DARAJA.LABS revolutionized how we make business decisions. Their team\'s deep understanding of our industry and technical prowess delivered insights we never thought possible.",
      author: "Sarah Johnson",
      position: "VP of Operations",
      company: "DataDriven Corp",
      companyLogo: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100&h=100&fit=crop",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      rating: 5
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  return (
    <section ref={ref} className="py-20 lg:py-32 bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-accent/5 to-transparent" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            What Our
            <span className="text-accent block lg:inline lg:ml-4">Clients Say</span>
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Don't just take our word for it. Here's what industry leaders say about their experience working with DARAJA.LABS.
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          className="relative max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative bg-card rounded-3xl p-8 lg:p-12 border border-border shadow-xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                {/* Quote */}
                <div className="mb-8">
                  <Icon name="Quote" size={48} className="text-accent/30 mx-auto mb-6" />
                  <p className="text-lg lg:text-xl text-card-foreground leading-relaxed max-w-4xl mx-auto">
                    "{testimonials?.[currentTestimonial]?.quote}"
                  </p>
                </div>

                {/* Rating */}
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials?.[currentTestimonial]?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={20} className="text-accent fill-current" />
                  ))}
                </div>

                {/* Author Info */}
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-accent/30">
                    <Image
                      src={testimonials?.[currentTestimonial]?.avatar}
                      alt={testimonials?.[currentTestimonial]?.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-card-foreground text-lg">
                      {testimonials?.[currentTestimonial]?.author}
                    </div>
                    <div className="text-accent text-sm">
                      {testimonials?.[currentTestimonial]?.position}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {testimonials?.[currentTestimonial]?.company}
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-lg overflow-hidden border border-border">
                    <Image
                      src={testimonials?.[currentTestimonial]?.companyLogo}
                      alt={`${testimonials?.[currentTestimonial]?.company} logo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-accent/10 hover:bg-accent/20 rounded-full flex items-center justify-center transition-all duration-200 focus-ring"
            >
              <Icon name="ChevronLeft" size={20} className="text-accent" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-accent/10 hover:bg-accent/20 rounded-full flex items-center justify-center transition-all duration-200 focus-ring"
            >
              <Icon name="ChevronRight" size={20} className="text-accent" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-3">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentTestimonial
                    ? 'bg-accent scale-125' :'bg-accent/30 hover:bg-accent/50'
                }`}
              />
            ))}
          </div>
        </motion.div>

        {/* Client Logos */}
        <motion.div
          className="mt-20 lg:mt-24"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-center text-lg text-muted-foreground mb-8">
            Trusted by industry leaders worldwide
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12 opacity-60">
            {testimonials?.map((testimonial) => (
              <div
                key={testimonial?.id}
                className="w-20 h-20 lg:w-24 lg:h-24 rounded-lg overflow-hidden border border-border hover:border-accent/30 transition-all duration-200 hover:opacity-100"
              >
                <Image
                  src={testimonial?.companyLogo}
                  alt={`${testimonial?.company} logo`}
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-200"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;